import React, { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import * as databaseIntegration from '../../services/databaseIntegration';
import { SharedWheel, ShareSettings, Wheel } from '../../types/wheel';
import { generateShareLink } from '../../utils/shareUtils';
import { trackWheelShare } from '../../utils/trackingUtils';

interface ShareWheelModalProps {
  isOpen: boolean;
  onClose: () => void;
  wheel: Wheel;
}

const ShareWheelModal: React.FC<ShareWheelModalProps> = ({ isOpen, onClose, wheel }) => {
  const [activeTab, setActiveTab] = useState<'configure' | 'link' | 'manage'>('configure');
  const [maxSpins, setMaxSpins] = useState<number | null>(10);
  const [isUnlimitedSpins, setIsUnlimitedSpins] = useState<boolean>(false);
  const [expirationOption, setExpirationOption] = useState<string>('never');
  const [accessCode, setAccessCode] = useState<string>('');
  const [shareLink, setShareLink] = useState<string>('');
  const [existingShares, setExistingShares] = useState<SharedWheel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load existing shares
  const loadExistingShares = useCallback(async () => {
    setIsLoading(true);
    try {
      const shares = await databaseIntegration.getWheelShares(wheel.id);
      setExistingShares(shares);
    } catch (error) {
      console.error('Error loading shares:', error);
      setError('Failed to load existing shares');
    } finally {
      setIsLoading(false);
    }
  }, [wheel.id]);

  // Load existing shares when the modal opens
  useEffect(() => {
    if (isOpen && wheel.id) {
      loadExistingShares();
    }
  }, [isOpen, wheel.id, loadExistingShares]);

  // Handle form submission
  const handleCreateShare = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Calculate expiration date based on selected option
      let expiresAt: Date | null = null;
      const now = new Date();

      if (expirationOption === '1hour') {
        expiresAt = new Date(now.getTime() + 60 * 60 * 1000);
      } else if (expirationOption === '4hours') {
        expiresAt = new Date(now.getTime() + 4 * 60 * 60 * 1000);
      } else if (expirationOption === '8hours') {
        expiresAt = new Date(now.getTime() + 8 * 60 * 60 * 1000);
      } else if (expirationOption === '1day') {
        expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      }

      // Create share settings
      const shareId = uuidv4();
      const shareSettings: ShareSettings = {
        maxSpins: isUnlimitedSpins ? null : maxSpins,
        expiresAt,
        accessCode: accessCode || null,
        isActive: true,
        createdAt: new Date(),
        allowedRoles: [],
        shareId,
      };

      // Create the share
      await databaseIntegration.createWheelShare(wheel.id, shareSettings);

      // Generate share link
      const link = generateShareLink(shareId);
      setShareLink(link);

      // Track wheel share event
      trackWheelShare(wheel.id, 'Link');

      // Show success message
      setSuccess('Share created successfully!');

      // Switch to link tab
      setActiveTab('link');

      // Reload existing shares
      loadExistingShares();
    } catch (error) {
      console.error('Error creating share:', error);
      setError('Failed to create share');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle copying link to clipboard
  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(shareLink)
      .then(() => {
        // Track wheel share event when link is copied
        if (wheel) {
          trackWheelShare(wheel.id, 'Clipboard');
        }
        setSuccess('Link copied to clipboard!');
        setTimeout(() => setSuccess(null), 3000);
      })
      .catch(err => {
        console.error('Failed to copy link:', err);
        setError('Failed to copy link');
      });
  };

  // Handle viewing a share
  const handleViewShare = (shareId: string) => {
    const link = generateShareLink(shareId);

    // Track wheel share event when link is opened
    if (wheel) {
      trackWheelShare(wheel.id, 'View');
    }

    window.open(link, '_blank');
  };

  // Handle deleting a share
  const handleDeleteShare = async (shareId: string) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Delete the share in the database
      await databaseIntegration.deleteWheelShare(wheel.id, shareId);

      // Update the UI
      setExistingShares(prevShares => prevShares.filter(share => share.shareId !== shareId));

      setSuccess('Share deleted successfully!');
    } catch (error) {
      console.error('Error deleting share:', error);
      setError('Failed to delete share');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle toggling share active status
  const handleToggleActive = async (shareId: string, isActive: boolean) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Update the share in the database
      await databaseIntegration.updateWheelShare(wheel.id, shareId, {
        isActive: !isActive,
      });

      // Update the UI
      setExistingShares(prevShares =>
        prevShares.map(share => {
          if (share.shareId === shareId) {
            return {
              ...share,
              settings: {
                ...share.settings,
                isActive: !isActive,
              },
            };
          }
          return share;
        })
      );

      setSuccess(`Share ${isActive ? 'deactivated' : 'activated'} successfully!`);
    } catch (error) {
      console.error('Error updating share:', error);
      setError('Failed to update share');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Share Wheel with Someone</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            &times;
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            className={`px-4 py-2 ${
              activeTab === 'configure'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-500'
            }`}
            onClick={() => setActiveTab('configure')}
          >
            Configure Control
          </button>
          <button
            className={`px-4 py-2 ${
              activeTab === 'link'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-500'
            }`}
            onClick={() => setActiveTab('link')}
            disabled={!shareLink}
          >
            Shared Link
          </button>
          <button
            className={`px-4 py-2 ${
              activeTab === 'manage'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-500'
            }`}
            onClick={() => setActiveTab('manage')}
          >
            Manage Shares
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-4">
          {/* Error and Success Messages */}
          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
              role="alert"
            >
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          {success && (
            <div
              className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4"
              role="alert"
            >
              <span className="block sm:inline">{success}</span>
            </div>
          )}

          {/* Configure Tab */}
          {activeTab === 'configure' && (
            <div>
              <div className="mb-4">
                <label htmlFor="maxSpins" className="block text-gray-700 text-sm font-bold mb-2">
                  Control How Many Times They Can Spin
                </label>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="unlimitedSpins"
                    checked={isUnlimitedSpins}
                    onChange={e => setIsUnlimitedSpins(e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="unlimitedSpins">Unlimited spins (let them keep going)</label>
                </div>
                {!isUnlimitedSpins && (
                  <input
                    id="maxSpins"
                    type="number"
                    value={maxSpins || ''}
                    onChange={e => setMaxSpins(parseInt(e.target.value) || null)}
                    min="1"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    placeholder="Enter maximum number of spins"
                  />
                )}
              </div>

              <div className="mb-4">
                <label
                  htmlFor="expirationOption"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Control How Long They Have Access
                </label>
                <select
                  id="expirationOption"
                  value={expirationOption}
                  onChange={e => setExpirationOption(e.target.value)}
                  className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  <option value="never">Never Expire</option>
                  <option value="1hour">1 Hour</option>
                  <option value="4hours">4 Hours</option>
                  <option value="8hours">8 Hours</option>
                  <option value="1day">1 Day</option>
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="accessCode" className="block text-gray-700 text-sm font-bold mb-2">
                  Make Them Beg for Access (Optional)
                </label>
                <input
                  id="accessCode"
                  type="text"
                  value={accessCode}
                  onChange={e => setAccessCode(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter access code"
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleCreateShare}
                  disabled={isLoading}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  {isLoading ? 'Creating...' : 'Share Wheel'}
                </button>
              </div>
            </div>
          )}

          {/* Link Tab */}
          {activeTab === 'link' && (
            <div>
              <div className="mb-4">
                <label htmlFor="shareLink" className="block text-gray-700 text-sm font-bold mb-2">
                  Link to Send to Your Sub
                </label>
                <div className="flex">
                  <input
                    id="shareLink"
                    type="text"
                    value={shareLink}
                    readOnly
                    className="shadow appearance-none border rounded-l w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-r focus:outline-none focus:shadow-outline"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-gray-600 text-sm">
                  Send this link to your submissive and watch them spin for your entertainment.
                  {accessCode && (
                    <span className="block mt-2">
                      <strong>Note:</strong> Your sub will need to beg you for the access code:{' '}
                      <strong>{accessCode}</strong>
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Manage Tab */}
          {activeTab === 'manage' && (
            <div>
              {isLoading ? (
                <div className="text-center py-4">Loading your subs&apos; data...</div>
              ) : existingShares.length === 0 ? (
                <div className="text-center py-4">No submissives have access yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Spins
                        </th>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {existingShares.map(share => (
                        <tr key={share.shareId}>
                          <td className="py-2 px-4 border-b border-gray-200">
                            {share.settings.createdAt instanceof Date
                              ? share.settings.createdAt.toLocaleDateString()
                              : share.settings.createdAt.toDate().toLocaleDateString()}
                          </td>
                          <td className="py-2 px-4 border-b border-gray-200">
                            <span
                              className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                                share.settings.isActive
                                  ? 'bg-indigo-100 text-indigo-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {share.settings.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-2 px-4 border-b border-gray-200">
                            {share.currentSpinCount} / {share.settings.maxSpins || '∞'}
                          </td>
                          <td className="py-2 px-4 border-b border-gray-200">
                            <button
                              onClick={() => handleViewShare(share.shareId)}
                              className="mr-2 text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 hover:bg-blue-200"
                            >
                              View
                            </button>
                            <button
                              onClick={() =>
                                handleToggleActive(share.shareId, share.settings.isActive)
                              }
                              className={`mr-2 text-xs px-2 py-1 rounded ${
                                share.settings.isActive
                                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                  : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
                              }`}
                            >
                              {share.settings.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => handleDeleteShare(share.shareId)}
                              className="text-xs px-2 py-1 rounded bg-red-100 text-red-800 hover:bg-red-200"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end p-4 border-t">
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareWheelModal;
